// src/pages/Team/PersonAvatar.jsx
// STATUS: active | UPDATED: 2026-09-27
// LOG:
//   2026-09-27  `badge` and `onClick`. The manager's morning puts a small green
//               tick on whoever warmed up, and tapping a face there opens the
//               give-a-mark sheet rather than navigating away.
//   2026-09-26  New. Tyler: the owner clicks the sales floor and it is just
//               avatars of the people, and clicking one opens their account.
//
// ONE FACE, DRAWN ONE WAY.
//
// There was no avatar component in this repo, so every surface that wanted to
// show a person drew initials in a circle by hand. This is that circle, once,
// so a face is the same size and the same colour everywhere and a person is
// recognisable across screens rather than restyled on each one.
//
// NO ROLE TAG ON THE AVATAR, and that is a rule rather than a layout choice.
// Labelling a person back at themselves is something this product has decided
// against before. A manager looking at their floor already knows who is who,
// and a badge reading EMPLOYEE over somebody's face is the kind of detail that
// makes software feel like a personnel file.
//
// The ring is the only state it carries, because it is the only state a
// manager scanning a floor actually needs at a glance.

import { Box, Flex, Text, VStack, Tooltip } from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router-dom'
import { initialsFor, displayNameFor, firstNameFor } from '../../lib/names.js'

// Waiting is the only thing worth colouring. It is the one state where somebody
// else is held up by this person's manager, and it is the reason to look at the
// floor at all. Everything else is just a face.
const RING = {
  waiting: 'accent.500',
  none: 'transparent',
}

export default function PersonAvatar({ person, size = 52, to, onClick, state = 'none', sub, badge, label }) {
  const name = displayNameFor(person)
  const first = firstNameFor(person) || name
  const initials = initialsFor(person)

  const circle = (
    <Flex
      boxSize={`${size}px`}
      borderRadius="full"
      bg="ink"
      color="onInk"
      align="center"
      justify="center"
      flexShrink={0}
      fontWeight={600}
      fontSize={`${Math.round(size * 0.36)}px`}
      letterSpacing="-0.01em"
      border="2px solid"
      borderColor={RING[state] || RING.none}
      // The ring sits outside the circle rather than eating into it, so a face
      // with a ring and a face without are the same size on the same row.
      boxShadow={state === 'waiting' ? '0 0 0 2px var(--chakra-colors-bg)' : 'none'}
      transition="transform 140ms ease, border-color 140ms ease"
      _groupHover={{ transform: 'translateY(-2px)' }}
    >
      {initials}
    </Flex>
  )

  // A badge sits on the rim at the bottom right, outside the circle's own
  // colour, so it reads as a thing added to the person rather than a change to
  // them. Only ever a small tick today.
  const face = badge ? (
    <Box position="relative" lineHeight={0}>
      {circle}
      <Box position="absolute" right="-2px" bottom="-2px">
        {badge}
      </Box>
    </Box>
  ) : (
    circle
  )

  const body = (
    <VStack spacing={2} minW={`${size + 16}px`} maxW={`${size + 40}px`}>
      {face}
      <VStack spacing={0}>
        <Text fontSize="sm" fontWeight={500} noOfLines={1} textAlign="center">
          {first}
        </Text>
        {sub && (
          <Text fontSize="xs" color="inkDim" noOfLines={1} textAlign="center">
            {sub}
          </Text>
        )}
      </VStack>
    </VStack>
  )

  if (onClick) {
    return (
      <Box
        as="button"
        type="button"
        onClick={onClick}
        role="group"
        aria-label={label || name}
        display="inline-flex"
        borderRadius="lg"
        p={2}
        _hover={{ bg: 'surface' }}
        _focusVisible={{ outline: '2px solid', outlineColor: 'accent.500', outlineOffset: '2px' }}
      >
        {body}
      </Box>
    )
  }

  if (!to) return body

  return (
    <Tooltip label={name} openDelay={400} hasArrow>
      <Box
        as={RouterLink}
        to={to}
        role="group"
        // 44px is the floor for a thumb and these are 52, but the LABEL is part
        // of the target too. Wrapping the whole stack rather than the circle
        // alone means tapping the name works, which is what people aim at.
        display="inline-flex"
        borderRadius="lg"
        p={2}
        _hover={{ textDecoration: 'none', bg: 'surface' }}
        _focusVisible={{ outline: '2px solid', outlineColor: 'accent.500', outlineOffset: '2px' }}
      >
        {body}
      </Box>
    </Tooltip>
  )
}
